/* eslint-disable react/no-array-index-key */
import { faCheck, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import BgImage from '@propertypal/shared/src/resources/images/premium/selling-your-property-bg.jpg';
import trackEvent from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { getMainImage } from '@propertypal/shared/src/utils/property';
import React, { Dispatch, FunctionComponent, useState } from 'react';
import { useTheme } from 'styled-components';
import Accordion from '../animation/Accordion';
import Button from '../buttons/Button';
import useMediaQuery from '../media-query/useMediaQuery';
import { LinkText } from '../typography';
import {
  AstrixText,
  Banner,
  BannerContent,
  BannerHeader,
  BannerImage,
  BannerText,
  BannerTitle,
  Column,
  ColumnBody,
  ColumnContainer,
  ColumnHeader,
  ColumnLabel,
  Content,
  ContentDivider,
  DisableOverlay,
  FeatureBubble,
  FooterText,
  List,
  ListHeader,
  ListRow,
  PremierBubble,
} from './AdFeature.style';

interface Props {
  property?: Property;
}

const PREMIER_LIST = (isRental?: boolean) => [
  (color: string) => (
    <p>
      <span style={{ color }}>Stand out</span> - with 4 photos on the search results page showing the best features of
      your property as chosen by you
    </p>
  ),
  (color: string) => (
    <p>
      Adverts appear above standard ads and are <span style={{ color }}>twice the size</span> of a standard ad
    </p>
  ),
  (color: string) => (
    <p>
      Premier properties receive <span style={{ color }}>3.5x more email enquiries</span> than standard ads
    </p>
  ),
  (color: string) => (
    <p>
      <span style={{ color }}>Reach more buyers</span> - push your advert up to the top of the search results every week
      for the first four weeks
    </p>
  ),
  (color: string) => (
    <p>
      <span style={{ color }}>Advertise until {isRental ? 'let' : 'sold'}</span> - with no limits or expiry date, your
      advert stays up until it&apos;s {isRental ? 'let' : 'sale'} agreed
    </p>
  ),
];

const FEATURED_LIST = [
  (color: string) => (
    <p>
      <span style={{ color }}>Stand out</span> - in an area specifically chosen by you for as long as you want
    </p>
  ),
  (color: string) => (
    <p>
      Adverts <span style={{ color }}>appear above</span> standard ads
    </p>
  ),
  (color: string) => (
    <p>
      Featured properties receive <span style={{ color }}>1.5x more email enquiries</span> than standard ads
    </p>
  ),
  (color: string) => (
    <p>
      <span style={{ color }}>Attract buyers</span> - your advert will appear at the top of the search results every
      week for the duration of your order
    </p>
  ),
  (color: string) => (
    <p>
      <span style={{ color }}>Upgrade your advert</span> - to a premier listing with no limit or expiry date
    </p>
  ),
];

const AdFeature: FunctionComponent<Props> = (props) => {
  const mainImage = props.property ? getMainImage(props?.property) : BgImage.src;
  const theme = useTheme();
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  const [premierListOpen, setPremierListOpen] = useState<boolean>(false);
  const [featureListOpen, setFeatureListOpen] = useState<boolean>(false);
  const isRental = props.property && props.property.saleType.key === 'rent';

  const renderListHeader = (color: string, onClick: Dispatch<boolean>, text: string) => {
    if (!isMobile) return null;

    return (
      <ListHeader color={color} onClick={() => onClick(true)}>
        <p>{text}</p>

        <FontAwesomeIcon icon={faChevronDown} color={color} />
      </ListHeader>
    );
  };

  return (
    <>
      <BannerHeader premiumPage={!props.property} data-testid="premium-banner-header">
        <BannerImage backgroundImage={mainImage} data-testid="premium-bg-image" />

        <Banner>UPGRADE YOUR LISTING</Banner>

        <BannerContent>
          <BannerTitle premiumPage={!props.property} data-testid="premium-title">
            {props.property ? `Are you ${isRental ? 'letting' : 'selling'} this property?` : 'Promote your property'}
          </BannerTitle>

          <BannerText>Our advertising upgrades can make your property really stand out.</BannerText>
        </BannerContent>
      </BannerHeader>

      <ColumnContainer premiumPage={!props.property}>
        <Column>
          {props.property && props.property.premierAvailable === false && (
            <DisableOverlay>
              <p>
                This property already has a premier listing. It can be booked again in{' '}
                {props.property.premierAvailableTimePeriod}.
              </p>
            </DisableOverlay>
          )}

          <ColumnLabel>BEST PERFORMING OPTION</ColumnLabel>
          <ColumnHeader color={theme.purple}>
            <h3>Premier Listing</h3>
            <p>Advertise until {isRental ? 'let' : 'sold'}</p>
          </ColumnHeader>

          <ColumnBody>
            <Content>
              <h5>Price</h5>
              <h3>£{isRental ? 24 : 49}.00</h3>
              <h4>Grab more attention with this larger advert, extra and bigger photos</h4>

              <a
                data-testid="premium-listing-link"
                onClick={() => trackEvent('Boost PDP Ad', 'Click', 'Premier')}
                href={
                  props?.property?.pathId
                    ? `/premium/premier-listing/${props.property.pathId}`
                    : `/premium/?type=premiumListing#selectproperty`
                }
              >
                <Button color={theme.purple} containerStyle={{ marginBottom: 15 }}>
                  Book a Premier Listing
                </Button>
              </a>

              <PremierBubble>
                <h3>350%</h3>
                <p>more enquiries</p>
              </PremierBubble>
            </Content>

            <Accordion
              header={renderListHeader(theme.purple, setPremierListOpen, 'What is included in a Premier Listing?')}
              hideHeaderOnToggle
              isOpen={!isMobile || premierListOpen}
            >
              <ContentDivider color={theme.purple}>
                <h4>PROMOTE YOUR PROPERTY</h4>
              </ContentDivider>

              <Content>
                <List>
                  {PREMIER_LIST(isRental).map((row, index) => (
                    <ListRow key={index}>
                      <FontAwesomeIcon icon={faCheck} color={theme.purple} />
                      {row(theme.purple)}
                    </ListRow>
                  ))}
                </List>
              </Content>
            </Accordion>
          </ColumnBody>
        </Column>

        <Column>
          {props.property && props.property.featurable === false && (
            <DisableOverlay>
              <p>Featured listing currently unavailable for this property.</p>
            </DisableOverlay>
          )}

          <ColumnHeader color={theme.primary} roundedTop>
            <h3>Featured Listing</h3>
            <p>You choose the duration</p>
          </ColumnHeader>

          <ColumnBody>
            <Content>
              <h5>Minimum Spend</h5>
              <h3>£{isRental ? 6 : 12}.00</h3>
              <h4>Attract buyers with this bigger advert and extra photos</h4>

              <a
                data-testid="featured-listing-link"
                onClick={() => trackEvent('Boost PDP Ad', 'Click', 'Featured')}
                href={
                  props?.property?.pathId
                    ? `/premium/featured-property/${props.property.pathId}`
                    : '/premium/?type=featuredListing#selectproperty'
                }
              >
                <Button color={theme.primary} containerStyle={{ marginBottom: 15 }}>
                  Book a Featured Listing
                </Button>
              </a>

              <FeatureBubble>
                <h3>150%</h3>
                <p>more enquiries</p>
              </FeatureBubble>
            </Content>

            <Accordion
              header={renderListHeader(theme.primary, setFeatureListOpen, 'What is included in a Featured Listing?')}
              hideHeaderOnToggle
              isOpen={!isMobile || featureListOpen}
            >
              <ContentDivider color={theme.primary}>
                <h4>PROMOTE YOUR PROPERTY</h4>
              </ContentDivider>

              <Content>
                <List>
                  {FEATURED_LIST.map((row, index) => (
                    <ListRow key={index}>
                      <FontAwesomeIcon icon={faCheck} color={theme.primary} />
                      {row(theme.primary)}
                    </ListRow>
                  ))}
                </List>
              </Content>
            </Accordion>
          </ColumnBody>
        </Column>
      </ColumnContainer>

      <AstrixText>Final cost of a featured listing is determined by search terms and duration of listing.</AstrixText>

      {props.property && (
        <FooterText data-testid="premium-footer">
          Not your property? <LinkText href="/premium">Click here to search for your property.</LinkText>
        </FooterText>
      )}
    </>
  );
};

export default React.memo(AdFeature);
